/**
 * Pure, React-free helpers for the Document Bank domain: id generation,
 * blank/edit draft factories, list parsing, validation, status metadata,
 * discovery filtering, a CLIENT-SIDE "semantic-ish" search over
 * title/summary/tags/context, freshness computation, sorting and formatting.
 *
 * "semantic-ish" search: we have no embedding index client-side, so this is a
 * transparent token-overlap + field-weighted scorer (title/tags weigh more than
 * body, exact phrase boosts, recency breaks ties). It is deterministic and
 * explainable — good enough to make a link bank discoverable, and a clean
 * extension point to swap in real embeddings later without changing callers.
 *
 * Deliberately NO productivity/velocity/leaderboard metric: documents are
 * indexed for discovery + knowledge retention, never counted or ranked per
 * author.
 */

import { v4 as uuid } from 'uuid';
import type { BadgeTone } from '../../ui';
import { DEFAULT_DOC_TYPE } from './documentConfig';
import type {
  DocumentFilter,
  DocumentStatus,
  TeamDocument,
  TeamDocumentDraft,
  TeamDocumentSummary,
} from './types';

/** Deterministic document id for a new team-document record. */
export function documentDocId(): string {
  return `doc_${uuid()}`;
}

/** Today's local date as yyyy-mm-dd. */
export function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Creates an empty draft for the register form (optionally seeding fields). */
export function blankDocumentDraft(
  seed: Partial<TeamDocumentDraft> = {},
): TeamDocumentDraft {
  return {
    title: '',
    docType: DEFAULT_DOC_TYPE,
    summary: '',
    contextWhy: '',
    authors: [],
    ownerPoc: '',
    reviewers: [],
    reviewedWith: [],
    status: 'DRAFT',
    createdDate: todayIso(),
    lastReviewedDate: undefined,
    documentLink: '',
    relatedServices: [],
    relatedRepo: undefined,
    relatedGoalId: undefined,
    relatedWorkstreamId: undefined,
    relatedStoryIds: [],
    relatedDecisionIds: [],
    tags: [],
    supersedesId: undefined,
    ...seed,
  };
}

/** Projects an existing document into an editable draft. */
export function draftFromDocument(doc: TeamDocument): TeamDocumentDraft {
  return {
    title: doc.title,
    docType: doc.docType,
    summary: doc.summary,
    contextWhy: doc.contextWhy,
    authors: doc.authors ?? [],
    ownerPoc: doc.ownerPoc,
    reviewers: doc.reviewers ?? [],
    reviewedWith: doc.reviewedWith ?? [],
    status: doc.status,
    createdDate: doc.createdDate,
    lastReviewedDate: doc.lastReviewedDate,
    documentLink: doc.documentLink,
    relatedServices: doc.relatedServices ?? [],
    relatedRepo: doc.relatedRepo,
    relatedGoalId: doc.relatedGoalId,
    relatedWorkstreamId: doc.relatedWorkstreamId,
    relatedStoryIds: doc.relatedStoryIds ?? [],
    relatedDecisionIds: doc.relatedDecisionIds ?? [],
    tags: doc.tags ?? [],
    supersedesId: doc.supersedesId,
  };
}

/**
 * Parses a comma / newline separated string into a normalized, de-duped list
 * (trimmed, empties dropped, original order preserved).
 */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (raw ?? '').split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins a list back into a comma-separated string for an input field. */
export function listToInput(list?: string[]): string {
  return (list ?? []).join(', ');
}

/** True when a string is a recognized status. */
export function isDocumentStatus(value: string): value is DocumentStatus {
  return (
    value === 'DRAFT' ||
    value === 'IN_REVIEW' ||
    value === 'APPROVED' ||
    value === 'DEPRECATED' ||
    value === 'ARCHIVED'
  );
}

/**
 * Validates a draft for save. A document record with no title, no link, or no
 * summary is not discoverable, so all three are required.
 */
export function validateDocumentDraft(draft: TeamDocumentDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('A document title is required.');
  if (!draft.documentLink.trim()) {
    problems.push('A document link is required — the bank indexes links, not content.');
  }
  if (!draft.summary.trim()) {
    problems.push('A summary is required — it powers discovery + search.');
  }
  if (!draft.docType.trim()) problems.push('A document type is required.');
  if (!isDocumentStatus(draft.status)) problems.push('Choose a valid status.');
  return problems;
}

/** Human-readable label for a status. */
export function statusLabel(status: DocumentStatus): string {
  switch (status) {
    case 'IN_REVIEW':
      return 'In review';
    case 'APPROVED':
      return 'Approved';
    case 'DEPRECATED':
      return 'Deprecated';
    case 'ARCHIVED':
      return 'Archived';
    case 'DRAFT':
    default:
      return 'Draft';
  }
}

/** Badge tone for a status. */
export function statusTone(status: DocumentStatus): BadgeTone {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'IN_REVIEW':
      return 'info';
    case 'DEPRECATED':
      return 'warning';
    case 'ARCHIVED':
      return 'neutral';
    case 'DRAFT':
    default:
      return 'accent';
  }
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Whole days since a document was last reviewed (or created, when never
 * reviewed). Undefined when no usable date exists.
 */
export function stalenessDays(
  doc: Pick<TeamDocument, 'lastReviewedDate' | 'createdDate'>,
): number | undefined {
  const ref = doc.lastReviewedDate || doc.createdDate;
  if (!ref) return undefined;
  const d = new Date(ref.length === 10 ? `${ref}T00:00:00` : ref);
  if (Number.isNaN(d.getTime())) return undefined;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / MS_PER_DAY));
}

/** Freshness threshold (days) beyond which a doc is flagged for re-review. */
export const STALE_THRESHOLD_DAYS = 180;

/** True when the document is older than the staleness threshold. */
export function isStale(
  doc: Pick<TeamDocument, 'lastReviewedDate' | 'createdDate' | 'status'>,
): boolean {
  if (doc.status === 'ARCHIVED' || doc.status === 'DEPRECATED') return false;
  const days = stalenessDays(doc);
  return days !== undefined && days > STALE_THRESHOLD_DAYS;
}

/** The 4-digit year of a document's createdDate, or '' when unparseable. */
export function documentYear(
  doc: Pick<TeamDocumentSummary, 'createdDate'>,
): string {
  const m = /^(\d{4})/.exec(doc.createdDate ?? '');
  return m ? m[1] : '';
}

/** Unique, sorted set of years present across the given documents. */
export function collectYears(items: TeamDocumentSummary[]): string[] {
  const set = new Set<string>();
  for (const d of items) {
    const y = documentYear(d);
    if (y) set.add(y);
  }
  return Array.from(set).sort((a, b) => b.localeCompare(a));
}

/** Unique, sorted set of a string-array facet (authors / tags / services). */
export function collectFacet(
  items: TeamDocumentSummary[],
  pick: (d: TeamDocumentSummary) => string[],
): string[] {
  const set = new Set<string>();
  for (const d of items) {
    for (const v of pick(d) ?? []) {
      const t = (v ?? '').trim();
      if (t) set.add(t);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Unique, sorted set of a scalar facet (ownerPoc), empties dropped. */
export function collectScalar(
  items: TeamDocumentSummary[],
  pick: (d: TeamDocumentSummary) => string | undefined,
): string[] {
  const set = new Set<string>();
  for (const d of items) {
    const t = (pick(d) ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * Applies the structured discovery filters (everything except the free-text
 * query). Empty fields match everything.
 */
export function filterDocuments(
  items: TeamDocumentSummary[],
  filter: DocumentFilter,
): TeamDocumentSummary[] {
  const has = (list: string[] | undefined, value: string) =>
    (list ?? []).some((v) => v.toLowerCase() === value.toLowerCase());
  const docType = filter.docType.trim();
  const status = filter.status.trim();
  const service = filter.service.trim();
  const author = filter.author.trim();
  const owner = filter.owner.trim();
  const reviewer = filter.reviewer.trim();
  const goalId = filter.goalId.trim();
  const tag = filter.tag.trim();
  const year = filter.year.trim();
  return items.filter((d) => {
    if (docType && d.docType !== docType) return false;
    if (status && d.status !== status) return false;
    if (service && !has(d.relatedServices, service)) return false;
    if (author && !has(d.authors, author)) return false;
    if (owner && (d.ownerPoc ?? '').toLowerCase() !== owner.toLowerCase()) return false;
    if (reviewer && !has(d.reviewers, reviewer)) return false;
    if (goalId && d.relatedGoalId !== goalId) return false;
    if (tag && !has(d.tags, tag)) return false;
    if (year && documentYear(d) !== year) return false;
    return true;
  });
}

/** Splits a query / field into lowercased word tokens (length >= 2). */
function tokenize(text: string): string[] {
  return (text.toLowerCase().match(/[a-z0-9]+/g) ?? []).filter(
    (t) => t.length >= 2,
  );
}

/** The searchable text corpus for a document, field-weighted by repetition. */
function corpusOf(d: TeamDocumentSummary): { title: string; body: string } {
  const title = [d.title, ...(d.tags ?? [])].join(' ');
  const body = [
    d.summary,
    d.contextWhy,
    d.docType,
    d.ownerPoc,
    ...(d.authors ?? []),
    ...(d.tags ?? []),
    ...(d.relatedServices ?? []),
    d.relatedRepo ?? '',
  ].join(' ');
  return { title, body };
}

/**
 * A transparent "semantic-ish" relevance score of a document against a free
 * text query. Token overlap with field weighting (title/tags 3x, body 1x) plus
 * an exact-phrase boost. Returns 0 when nothing matches.
 *
 * EXTENSION POINT: swap this token scorer for a real embedding similarity
 * (cosine over stored vectors) without changing {@link searchDocuments} or any
 * caller — the (doc, query) -> number contract is stable.
 */
export function scoreDocument(d: TeamDocumentSummary, query: string): number {
  const terms = tokenize(query);
  if (terms.length === 0) return 0;
  const { title, body } = corpusOf(d);
  const titleTokens = new Set(tokenize(title));
  const bodyTokens = new Set(tokenize(body));
  let score = 0;
  for (const term of terms) {
    if (titleTokens.has(term)) score += 3;
    else if (bodyTokens.has(term)) score += 1;
  }
  // Exact phrase boost.
  const q = query.trim().toLowerCase();
  if (q.length >= 3) {
    if (title.toLowerCase().includes(q)) score += 4;
    else if (body.toLowerCase().includes(q)) score += 2;
  }
  return score;
}

/**
 * Ranks documents by {@link scoreDocument} for a free-text query, dropping
 * zero-score docs. Recency (createdAt) breaks ties. When the query is blank,
 * returns the input unchanged (the caller applies its own default sort).
 */
export function searchDocuments(
  items: TeamDocumentSummary[],
  query: string,
): TeamDocumentSummary[] {
  if (!query.trim()) return items;
  return items
    .map((d) => ({ d, s: scoreDocument(d, query) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => {
      if (b.s !== a.s) return b.s - a.s;
      return (b.d.createdAt ?? '').localeCompare(a.d.createdAt ?? '');
    })
    .map((x) => x.d);
}

/** Default sort: most-recently-created first (by author-supplied createdDate, then createdAt). */
export function sortDocuments(
  items: TeamDocumentSummary[],
): TeamDocumentSummary[] {
  return items.slice().sort((a, b) => {
    const byDate = (b.createdDate ?? '').localeCompare(a.createdDate ?? '');
    if (byDate !== 0) return byDate;
    return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
  });
}

/** Formats a yyyy-mm-dd (or ISO) date as a compact local date. */
export function formatDate(date?: string): string {
  if (!date) return '';
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
